import { useAuth } from './useAuth';
import { logoutClient } from './api';
import { useLocation, useNavigate } from 'react-router-dom';
import { canAccessMetsEnterprise } from './metsEnterpriseAccess';
import MetsEnterpriseSubmenu from './MetsEnterpriseSubmenu';
import AdminSubmenu from './AdminSubmenu';
import { canAccessManning, canAccessUploadData, isModuleLimitedUser } from './accessPolicy';

export const useNavigation = (theme) => {
    const { groups, isStaff, isAuthenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Common styles for menu items
    const getMenuStyles = () => {
        const isDark = theme === 'dark';
        return {
            menuItem: {
                display: 'block',
                padding: '12px 20px',
                color: isDark ? '#e0e0e0' : '#333',
                textDecoration: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s',
            },
            menuItemButton: {
                display: 'block',
                padding: '12px 20px',
                color: isDark ? '#e0e0e0' : '#333',
                textDecoration: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s',
            }
        };
    };

    const handleLogout = () => {
        logoutClient();
        navigate('/login');
    };

    const safeGroups = Array.isArray(groups) ? groups : [];
    const styles = getMenuStyles();

    // Generate menu items based on user permissions and current route
    const generateMenuItems = () => {
        const menuItems = [];

        // Home - always available
        if (location.pathname !== '/') {
            menuItems.push(
                <button
                    key="home"
                    style={styles.menuItem}
                    onClick={() => navigate('/')}
                >
                    Home
                </button>
            );
        }

        const limitedModules = isModuleLimitedUser({ isStaff, groups: safeGroups });
        if (limitedModules) {
            if (canAccessManning({ isStaff, groups: safeGroups }) && location.pathname !== '/manning') {
                menuItems.push(
                    <button
                        key="manning"
                        style={styles.menuItem}
                        onClick={() => navigate('/manning')}
                    >
                        Manning
                    </button>
                );
            }
            if (canAccessUploadData({ isStaff, groups: safeGroups }) && location.pathname !== '/upload-data') {
                menuItems.push(
                    <button
                        key="upload-data"
                        style={styles.menuItem}
                        onClick={() => navigate('/upload-data')}
                    >
                        Upload Data
                    </button>
                );
            }
        } else {

        // Inspections Dashboard - always available
        if (location.pathname !== '/inspections') {
            menuItems.push(
                <button
                    key="inspections"
                    style={styles.menuItem}
                    onClick={() => navigate('/inspections')}
                >
                    Inspections
                </button>
            );
        }

        // Aircraft Status Board and Summary - for staff, supervisors, and viewers
        if ((isStaff || safeGroups.includes('Supervisor') || safeGroups.includes('Viewer'))) {
            // Aircraft Status Board
            if (location.pathname !== '/status-board') {
                menuItems.push(
                    <button
                        key="status-board"
                        style={styles.menuItem}
                        onClick={() => navigate('/status-board')}
                    >
                        Aircraft Status Board
                    </button>
                );
            }

            // Aircraft Summary
            if (location.pathname !== '/aircraft-summary') {
                menuItems.push(
                    <button
                        key="aircraft-summary"
                        style={styles.menuItem}
                        onClick={() => navigate('/aircraft-summary')}
                    >
                        Aircraft Summary
                    </button>
                );
            }
        }

        // METS Enterprise — nested submenu (staff or METS Enterprise / Supervisor)
        if (canAccessMetsEnterprise({ isStaff, groups: safeGroups })) {
            menuItems.push(
                <MetsEnterpriseSubmenu key="mets-enterprise" theme={theme} />
            );
        }

        if ((isStaff || safeGroups.includes('AI')) && location.pathname !== '/legion') {
            menuItems.push(
                <button
                    key="legion"
                    style={styles.menuItem}
                    onClick={() => navigate('/legion')}
                >
                    Legion
                </button>
            );
        }

        // Reports hub - for staff and supervisors only
        if ((isStaff || safeGroups.includes('Supervisor'))) {
            if (location.pathname !== '/reports' && !location.pathname.startsWith('/reports/')) {
                menuItems.push(
                    <button
                        key="reports"
                        style={styles.menuItem}
                        onClick={() => navigate('/reports')}
                    >
                        Reports
                    </button>
                );
            }

            // Daily Aircraft Status Report
            if (location.pathname !== '/daily-status-report') {
                menuItems.push(
                    <button
                        key="daily-status-report"
                        style={styles.menuItem}
                        onClick={() => navigate('/daily-status-report')}
                    >
                        Daily Status Report
                    </button>
                );
            }

            // SLMM - Site Level Maintenance Management
            if (location.pathname !== '/slmm') {
                menuItems.push(
                    <button
                        key="slmm"
                        style={styles.menuItem}
                        onClick={() => navigate('/slmm')}
                    >
                        SLMM
                    </button>
                );
            }

            if (location.pathname !== '/parts') {
                menuItems.push(
                    <button
                        key="parts"
                        style={styles.menuItem}
                        onClick={() => navigate('/parts')}
                    >
                        Parts
                    </button>
                );
            }

            // Executive Dashboard - Staff or Supervisors (gated by the parent block)
            if (location.pathname !== '/executive-dashboard') {
                menuItems.push(
                    <button
                        key="executive-dashboard"
                        style={styles.menuItem}
                        onClick={() => navigate('/executive-dashboard')}
                    >
                        Executive Dashboard
                    </button>
                );
            }
        }

        // Aircraft Maintenance Prioritization Tool (A.M.P.T) - AMPT group or Staff
        if ((isStaff || safeGroups.includes('AMPT')) && location.pathname !== '/maintenance-priority') {
            menuItems.push(
                <button
                    key="maintenance-priority"
                    style={styles.menuItem}
                    onClick={() => navigate('/maintenance-priority')}
                >
                    AMPT
                </button>
            );
        }

        // Manning - Staff, Supervisor, or Manning group
        if ((isStaff || safeGroups.includes('Supervisor') || safeGroups.includes('Manning')) && location.pathname !== '/manning') {
            menuItems.push(
                <button
                    key="manning"
                    style={styles.menuItem}
                    onClick={() => navigate('/manning')}
                >
                    Manning
                </button>
            );
        }

        // Flight Planning - Staff, Supervisor, AMPT, Scheduler, or Flight Planning group
        if ((isStaff || safeGroups.includes('Supervisor') || safeGroups.includes('AMPT') || safeGroups.includes('Scheduler') || safeGroups.includes('Flight Planning')) && location.pathname !== '/flight-planning') {
            menuItems.push(
                <button
                    key="flight-planning"
                    style={styles.menuItem}
                    onClick={() => navigate('/flight-planning')}
                >
                    Flight Planning
                </button>
            );
        }

        // Hangar Bay Locations - Staff and AMSS group
        if ((isStaff || safeGroups.includes('AMSS')) && location.pathname !== '/hangar-bays') {
            menuItems.push(
                <button
                    key="hangar-bays"
                    style={styles.menuItem}
                    onClick={() => navigate('/hangar-bays')}
                >
                    Hangar Bays
                </button>
            );
        }

        // Aircraft Maintenance Spot Sheet - Staff and AMSS group
        if ((isStaff || safeGroups.includes('AMSS')) && location.pathname !== '/aircraft-parking') {
            menuItems.push(
                <button
                    key="aircraft-parking"
                    style={styles.menuItem}
                    onClick={() => navigate('/aircraft-parking')}
                >
                    AMSS
                </button>
            );
        }

        // Control Tower (ADS-B) - Staff and AMSS group
        if ((isStaff || safeGroups.includes('AMSS')) && location.pathname !== '/control-tower') {
            menuItems.push(
                <button
                    key="control-tower"
                    style={styles.menuItem}
                    onClick={() => navigate('/control-tower')}
                >
                    Control Tower
                </button>
            );
        }

        // Upload Data - Staff, Supervisor, or Upload group
        if ((isStaff || safeGroups.includes('Supervisor') || safeGroups.includes('Upload')) && location.pathname !== '/upload-data') {
            menuItems.push(
                <button
                    key="upload-data"
                    style={styles.menuItem}
                    onClick={() => navigate('/upload-data')}
                >
                    Upload Data
                </button>
            );
        }

        // Staff-only admin routes — nested submenu
        if (isStaff) {
            menuItems.push(
                <AdminSubmenu key="admin" theme={theme} />
            );
        }

        } // end full-app menu for non-limited users

        // Change Password - always available
        if (location.pathname !== '/change-password') {
            menuItems.push(
                <button
                    key="change-password"
                    style={styles.menuItem}
                    onClick={() => navigate('/change-password')}
                >
                    Change Password
                </button>
            );
        }

        // Logout - always available
        menuItems.push(
            <button
                key="logout"
                style={styles.menuItemButton}
                onClick={handleLogout}
            >
                Logout
            </button>
        );

        return menuItems;
    };

    return {
        menuItems: generateMenuItems(),
        isAuthenticated,
        groups: safeGroups,
        isStaff
    };
};
